import { handle } from 'redux-pack';
import { Action } from '../../../types/actions';
import type { BrandliveEvent, EmailSubscriptions, IScheduledEmailWithEventName, PasscodeListMemberType, SentScheduledEmailsWithEventName } from '../../../types/working-model';
import { type PasscodeListMember } from '../../../types/working-model';
import {
	GET_PASSCODE_LIST_MEMBERS,
	SEARCH_MEMBERS,
	ADD_SELECTED_MEMBER,
	REMOVE_SELECTED_MEMBER,
	SEARCH_MEMBER_COUNT,
	REMOVE_ALL_SELECTED_MEMBERS,
	GET_AUDIENCE_INVITE_STATUS,
	REMOVE_ALL_MEMBERS,
	type PasscodeListMemberActions,
} from '../../actions/admin/passcode-list-members';
import type { AudienceMemberMinimal, MergedAudienceMember } from '@/types/audience-list-members.types';
import { audienceListMemberToMergedAudienceMember, passcodeListMemberToMergedAudienceMember } from '@/store/utils/audience-members';

export interface PasscodeListMembersState {
	members: MergedAudienceMember[];
	selectedMembers: MergedAudienceMember[];
	member_count: number;
	loadingMembers: boolean;
	audienceInvitesStatuses: Record<string, SentScheduledEmailsWithEventName[]> | null;
	fetchingInvites: boolean;
	eventsWithoutInvites: BrandliveEvent[];
	unsubscribedUsers: Record<string, EmailSubscriptions>;
	pendingInvites: IScheduledEmailWithEventName[];
}

const initialState: PasscodeListMembersState = {
	audienceInvitesStatuses: null,
	eventsWithoutInvites: [],
	fetchingInvites: false,
	loadingMembers: false,
	member_count: 0,
	members: [],
	pendingInvites: [],
	selectedMembers: [],
	unsubscribedUsers: {},
};

export default function PasscodeListMembersReducer(
	state: PasscodeListMembersState = initialState,
	action: PasscodeListMemberActions
): PasscodeListMembersState {
	switch (action.type) {
		case GET_PASSCODE_LIST_MEMBERS: {
			return handle(state, action, {
				start: state => ({ ...state, loadingMembers: true }),
				success: (state) => {
					// there are members in the response
					if (action.payload?.[0]) {
						// this member has a bl_profile key, meaning it must be an audience list member
						if ('bl_profile' in action.payload[0]) {
							return {
								...state,
								members: (action.payload as AudienceMemberMinimal[]).map(audienceListMemberToMergedAudienceMember)
							};
						}

						return {
							...state,
							members: (action.payload as PasscodeListMemberType[]).map(passcodeListMemberToMergedAudienceMember)
						};
					}

					return {
						...state,
						members: []
					};
				},
				finish: state => ({ ...state, loadingMembers: false }),
			});
		}
		case SEARCH_MEMBERS: {
			return handle(state, action, {
				start: state => ({ ...state, loadingMembers: true }),
				success: (state) => {
					// there are members in the response
					if (action.payload?.[0]) {
						// this member has a bl_profile key, meaning it must be an audience list member
						if ('bl_profile' in action.payload[0]) {
							return {
								...state,
								members: (action.payload as AudienceMemberMinimal[]).map(audienceListMemberToMergedAudienceMember)
							};
						}

						return {
							...state,
							members: (action.payload as PasscodeListMember[]).map(passcodeListMemberToMergedAudienceMember)
						};
					}

					return {
						...state,
						members: []
					};
				},
				finish: state => ({ ...state, loadingMembers: false }),
			});
		}
		case SEARCH_MEMBER_COUNT: {
			return handle(state, action, {
				success: (state) => ({
					...state,
					member_count: Number(action.payload?.[0]?.member_count ?? 0),
				}),
			});
		}
		case ADD_SELECTED_MEMBER: {
			return {
				...state,
				selectedMembers: [...state.selectedMembers, action.payload],
			};
		}

		case REMOVE_SELECTED_MEMBER: {
			return {
				...state,
				selectedMembers: state.selectedMembers.filter(
					(member) =>
						member.member_id !== action.payload.member_id
				),
			};
		}

		case REMOVE_ALL_SELECTED_MEMBERS: {
			return {
				...state,
				selectedMembers: [],
			};
		}

		case REMOVE_ALL_MEMBERS: {
			return {
				...state,
				selectedMembers: [],
				member_count: 0,
				members: [],
				loadingMembers: false,
			};
		}

		case GET_AUDIENCE_INVITE_STATUS: {
			return handle(state, action, {
				start: state => ({ ...state, fetchingInvites: true }),
				success: (state) => {
					const pendingInvites = action.payload?.pendingInvites || [];
					const invites = action.payload?.invites || [];
					const eventsWithoutInvites = action.payload?.eventsWithoutInvites || [];
					const unsubscribedUsers = action.payload?.unsubscribedUsers || [];

					const unsubscribedMap = unsubscribedUsers.reduce((accum: Record<string, EmailSubscriptions>, curr: EmailSubscriptions) => {
						if (!curr.recipient) return accum;
						return {
							...accum,
							[curr.recipient]: curr
						};
					}, {});

					const groupByEmail: Record<string, SentScheduledEmailsWithEventName[]> = {};
					invites?.forEach?.((invite: SentScheduledEmailsWithEventName) => {
						if (groupByEmail[invite.email]) {
							groupByEmail[invite.email].push(invite);
						} else {
							groupByEmail[invite.email] = [invite];
						}
					});
					return {
						...state,
						audienceInvitesStatuses: groupByEmail,
						fetchingInvites: false,
						eventsWithoutInvites,
						unsubscribedUsers: unsubscribedMap,
						pendingInvites,
					};
				},
				finish: state => ({ ...state, fetchingInvites: false }),
				failure: state => ({ ...state, fetchingInvites: false }),
			});
		}

		default:
			return state;
	}
}
